import { ReactElement, useEffect, useRef, useState } from "react";
import { Box, Grid, IconButton, SxProps, Theme, Typography } from "@mui/material";
import { Button } from "../../../../reusable-components";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import Progress from "../../../../common/progress";
import { operatorAdviceErrorOccurred, operatorAdviceScrollToTop } from "../../../../App";
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { PoolXNumberFormat } from "../../../../common/CurrencyFormatter";
import { IsComeOn, IsLeo, themeC } from "../../../../theme/themehelper";
import { ComeOnReceipt } from "../../../../images/comeonicons";
import { UnibetReceiptIcon } from "../../../../images/unibeticons";
import { track } from "../../../../analytics/analytics";

/* ------------------------------------------------------------------ */
/*  Sx styles                                                         */
/* ------------------------------------------------------------------ */

const overlayBackdropSx: SxProps<Theme> = {
  position: "fixed",
  background: "rgba(6,6,6,0.5)",
  top: 0,
  left: 0,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 90,
  height: "100%",
  width: "100%",
};

const clickTrapSx: SxProps<Theme> = {
  position: "absolute",
  width: "100%",
  height: "100%",
};

const confirmWindowSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  backgroundColor: "var(--background_2)",
  borderRadius: "var(--radius)",
  padding: "12px 12px 24px 12px",
  width: 300,
  minHeight: 160,
  position: "absolute",
  top: 250,
  zIndex: 99,
};

const flexEndSx: SxProps<Theme> = {
  display: "flex",
  justifyContent: "flex-end",
  width: "100%",
};

const iconHolderSx: SxProps<Theme> = {
  fontSize: "60px",
  margin: "5px 0px 5px 0px",
};

const messageSx: SxProps<Theme> = {
  fontWeight: "bold",
  fontSize: "14px",
  margin: "20px 0",
};

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

export interface ErrorResponse {
  localizedOperatorMessage?: string;
  errorCode?: number;
  operatorErrorCode?: number;
}

function getErrorMessage(errorResponse: ErrorResponse, t: (key: string) => string): string {
  if (errorResponse.localizedOperatorMessage !== undefined && errorResponse.localizedOperatorMessage !== "") {
    return errorResponse.localizedOperatorMessage;
  }
  if (errorResponse.errorCode === 101) {
    return t("unable_to_complete_purchase");
  }
  if (errorResponse.errorCode === 102) {
    return t("insufficient_funds");
  }
  if (errorResponse.errorCode === 105) {
    return t("gaming_limits_reached");
  }
  return "";
}

/* ------------------------------------------------------------------ */
/*  RenderSwitch                                                      */
/* ------------------------------------------------------------------ */

interface FreeBet {
  numFull: number;
  numHalf: number;
}

interface RenderSwitchProps {
  submitting: boolean;
  submitFailed: boolean;
  amount: number;
  currency: string;
  handleClick: () => void;
  submitError: ErrorResponse | undefined;
  t: (key: string) => string;
  freebet?: FreeBet;
  stakePerRow: number;
}

function RenderSwitch({
  submitting,
  submitFailed,
  amount,
  currency,
  handleClick,
  submitError,
  t,
  freebet,
  stakePerRow,
}: RenderSwitchProps): ReactElement {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const locale = t("locale");
  const nf = PoolXNumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  if (submitFailed && submitError) {
    const errorMessage = getErrorMessage(submitError, t);
    return (
      <Box>
        <Typography sx={{ marginTop: "30px", textAlign: "center" }}>
          {t("unable_to_complete_purchase")}, {t("error_code")}: {submitError.errorCode}
        </Typography>
        <Typography sx={{ textAlign: "center" }}>{errorMessage}</Typography>
      </Box>
    );
  }

  if (submitting) {
    return <Progress isSmallScreen={false} />;
  }

  if (freebet !== undefined) {
    const freebetAmount = Math.pow(3, freebet.numFull) * Math.pow(2, freebet.numHalf) * stakePerRow;
    return (
      <>
        <Typography sx={messageSx}>
          {t("do_you_want_to_place_freebet")} {nf.format(freebetAmount)}?
        </Typography>
        <Button type="submit" width="200px" onClick={handleClick}>
          {t("pay_freebet")}
        </Button>
      </>
    );
  }

  return (
    <>
      <Typography sx={messageSx}>
        {t("do_you_want_to_place_bet")} {nf.format(amount)}?
      </Typography>
      <Button type="submit" width="200px" onClick={handleClick}>
        {t("pay")}
      </Button>
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  ConfirmPayment                                                    */
/* ------------------------------------------------------------------ */

interface ConfirmPaymentProps {
  amount: number;
  currency: string;
  toggle: (show: boolean) => void;
  submitting: boolean;
  submitFailed: boolean;
  submitError: ErrorResponse | undefined;
  freebet?: FreeBet;
  stakePerRow: number;
  handleCloseDialog: () => void;
}

function ConfirmPayment({
  amount,
  currency,
  toggle,
  submitting,
  submitFailed,
  submitError,
  freebet,
  stakePerRow,
  handleCloseDialog,
}: ConfirmPaymentProps): ReactElement {
  const [, setShouldRenderConfirm] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [iconColor, setIconColor] = useState("black");
  const { t } = useTranslation();

  useEffect(() => {
    if (wrapperRef.current) {
      const styles = getComputedStyle(wrapperRef.current);
      const color = styles.getPropertyValue("--text-black").trim();
      if (window.theme === themeC.ComeOn) {
        setIconColor("#C0F9C8");
      } else if (color) {
        setIconColor(color);
      }
    }
  }, []);

  const handleClose = (): void => {
    if (!submitFailed && !submitting) {
      track("bet_cancelled", { value: amount, currency });
    }
    toggle(false);
    handleCloseDialog();
  };

  useEffect(() => {
    if (!submitFailed) return;

    const errorCode = Number(submitError?.errorCode);
    const operatorErrorCode = Number(submitError?.operatorErrorCode);
    const errorMessage = submitError ? getErrorMessage(submitError, t) : "";

    track("bet_failed", { error_code: submitError?.errorCode, operator_error_code: submitError?.operatorErrorCode, value: amount, currency });

    operatorAdviceErrorOccurred({
      errorCode: submitError?.errorCode,
      errorMessage,
      operatorErrorCode: submitError?.operatorErrorCode,
    });

    if (submitFailed && IsLeo(window.theme) && errorCode === 104 && operatorErrorCode === 3) {
      handleClose();
    }
  }, [submitFailed, submitError]);

  const handleClick = (): void => {
    setShouldRenderConfirm(true);
    operatorAdviceScrollToTop(500);
  };

  return (
    <Box ref={wrapperRef} sx={overlayBackdropSx}>
      <Box sx={clickTrapSx} onClick={() => {}} />
      <Box sx={confirmWindowSx}>
        <Box sx={flexEndSx}>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon sx={{ color: iconColor, cursor: "pointer" }} />
          </IconButton>
        </Box>

        <Box sx={iconHolderSx}>
          {submitFailed ? (
            <ErrorOutlineIcon fontSize="inherit" />
          ) : window.theme === themeC.Green ? (
            <UnibetReceiptIcon width={60} height={60} fill={iconColor} />
          ) : IsComeOn(window.theme) ? (
            <Grid>{ComeOnReceipt(51, 50, iconColor)}</Grid>
          ) : (
            <ReceiptOutlinedIcon sx={{ color: iconColor }} fontSize="inherit" />
          )}
        </Box>

        <RenderSwitch
          submitting={submitting}
          submitFailed={submitFailed}
          amount={amount}
          currency={currency}
          handleClick={handleClick}
          submitError={submitError}
          t={t}
          freebet={freebet}
          stakePerRow={stakePerRow}
        />
      </Box>
    </Box>
  );
}

export default ConfirmPayment;
